import type { ProductAnalysisEconomicsSection } from '@ozon-ai/analysis/contracts';
import {
  cogsSourceLabel,
  economicsExpenseKeyLabel,
  formatAnalysisDate,
  formatRub,
} from './productAnalysisLabels';

const muted = 'text-xs text-muted-foreground';

function signedExpenseLine(label: string, signedAmount: number): string {
  if (signedAmount === 0) return `${label}: 0 ₽`;
  const display = formatRub(Math.abs(signedAmount));
  return signedAmount < 0 ? `${label}: −${display} ₽` : `${label}: +${display} ₽`;
}

export function ProductAnalysisEconomicsDetail({
  economics,
  netSoldUnits,
}: {
  economics: ProductAnalysisEconomicsSection;
  netSoldUnits?: number | null;
}) {
  const cogs = economics.cogs;
  const netRevenue = economics.netRevenue;
  const netProfit = economics.netProfit;
  const costOfGoodsSold = economics.costOfGoodsSold;
  const operational = economics.ozonOperationalBreakdown;
  const operationalTotal = economics.ozonOperationalExpenses ?? 0;

  const avgRevenuePerUnit =
    economics.netRevenuePerUnit ??
    (netRevenue != null && netSoldUnits != null && netSoldUnits > 0 ? netRevenue / netSoldUnits : null);
  const taxBase =
    economics.tax?.base ??
    (economics.tax?.regime === 'usn_income' || economics.tax?.regime === 'percent_of_revenue'
      ? netRevenue ?? null
      : economics.tax?.regime === 'usn_income_expense'
        ? economics.profitBeforeTax ?? null
        : null);
  const grossAccrualRevenue = economics.grossAccrualRevenue;
  const revenueStorno = economics.revenueStorno ?? 0;

  return (
    <div className="mt-2 flex flex-col gap-2 border-t border-border pt-2">
      <p className="text-sm font-semibold">Как посчитана экономика</p>

      <p className={muted}>
        Чистая выручка — начисления за продажи плюс сторно возвратов. Это не цена на витрине и не
        валовые начисления, делённые на чистые штуки.
      </p>

      {avgRevenuePerUnit != null && netSoldUnits != null && netSoldUnits > 0 && (
        <p className={muted}>
          Средняя чистая выручка на успешную продажу: {formatRub(netRevenue ?? 0)} / {netSoldUnits} шт. ≈{' '}
          {formatRub(avgRevenuePerUnit)} ₽/шт.
        </p>
      )}

      {economics.profitPerUnit != null && netSoldUnits != null && netSoldUnits > 0 && (
        <p className={muted}>
          Чистая прибыль на единицу: {formatRub(economics.profitPerUnit)} ₽/шт. (это не средняя цена
          продажи)
        </p>
      )}

      {cogs ? (
        <div className="flex flex-col gap-1">
          {cogs.segments && cogs.segments.length > 1 ? (
            <>
              <p className={muted}>COGS по датам начислений:</p>
              {cogs.segments.map((segment) => (
                <p key={`${segment.effectiveFrom}-${segment.unitCost}-${segment.purchaseId ?? ''}`} className={muted}>
                  с {formatAnalysisDate(segment.effectiveFrom)}: {segment.units} шт. × {formatRub(segment.unitCost)}{' '}
                  {segment.currency} = {formatRub(segment.amountRub)} ₽
                  {segment.purchaseId ? ` (закупка ${segment.purchaseId.slice(0, 8)}…)` : ''}
                </p>
              ))}
              <p className={muted}>
                Итого COGS: {costOfGoodsSold != null ? `${formatRub(costOfGoodsSold)} ₽` : '—'} ({cogs.units} шт.,
                средняя {formatRub(cogs.unitCost)} ₽/шт.)
              </p>
            </>
          ) : (
            <>
              <p className={muted}>
                Себестоимость за единицу: {formatRub(cogs.unitCost)} {cogs.currency} (
                {cogsSourceLabel(cogs.source)}
                {cogs.purchaseId ? `, закупка ${cogs.purchaseId.slice(0, 8)}…` : ''})
              </p>
              <p className={muted}>
                COGS: {cogs.units} шт. × {formatRub(cogs.unitCost)} ={' '}
                {costOfGoodsSold != null ? `${formatRub(costOfGoodsSold)} ₽` : '—'}
              </p>
              <p className={muted}>Действует с: {formatAnalysisDate(cogs.costAsOf)}</p>
            </>
          )}
        </div>
      ) : (
        <p className={muted}>
          Себестоимость не найдена в Procurement — COGS и ROI не считаются.
        </p>
      )}

      <div className="flex flex-col gap-1">
        <p className="text-xs font-semibold text-foreground">P&amp;L по SKU (начисления Ozon)</p>
        {grossAccrualRevenue != null && (
          <p className={muted}>
            {signedExpenseLine('Начисления за продажи', grossAccrualRevenue)}
          </p>
        )}
        {economics.revenueStorno != null && (
          <p className={muted}>
            {signedExpenseLine('Сторно выручки возвратов', revenueStorno)}
          </p>
        )}
        {netRevenue != null && (
          <p className={muted}>
            {signedExpenseLine('Чистая выручка', netRevenue)}
          </p>
        )}
        {(economics.compensations ?? 0) !== 0 && (
          <p className={muted}>
            {signedExpenseLine('Компенсации', economics.compensations ?? 0)}
          </p>
        )}
        {(economics.adjustments ?? 0) !== 0 && (
          <p className={muted}>
            {signedExpenseLine('Корректировки', economics.adjustments ?? 0)}
          </p>
        )}
        {operational && (
          <>
            <p className={muted}>
              Расходы Ozon по SKU (комиссия, доставка, эквайринг и др.):{' '}
              {operationalTotal > 0 ? `−${formatRub(operationalTotal)} ₽` : '0 ₽ — см. предупреждение ниже'}
            </p>
            {operational.commission > 0 && (
              <p className={`${muted} pl-2`}>
                • {economicsExpenseKeyLabel.commission}: {formatRub(operational.commission)} ₽
              </p>
            )}
            {operational.logistics > 0 && (
              <p className={`${muted} pl-2`}>
                • {economicsExpenseKeyLabel.logistics}: {formatRub(operational.logistics)} ₽
              </p>
            )}
            {operational.last_mile > 0 && (
              <p className={`${muted} pl-2`}>
                • {economicsExpenseKeyLabel.last_mile}: {formatRub(operational.last_mile)} ₽
              </p>
            )}
            {operational.acquiring > 0 && (
              <p className={`${muted} pl-2`}>
                • {economicsExpenseKeyLabel.acquiring}: {formatRub(operational.acquiring)} ₽
              </p>
            )}
            {operational.posting_processing > 0 && (
              <p className={`${muted} pl-2`}>
                • {economicsExpenseKeyLabel.posting_processing}: {formatRub(operational.posting_processing)} ₽
              </p>
            )}
            {operational.storage > 0 && (
              <p className={`${muted} pl-2`}>
                • {economicsExpenseKeyLabel.storage}: {formatRub(operational.storage)} ₽
              </p>
            )}
          </>
        )}
        {(economics.returnExpenses ?? 0) > 0 && (
          <p className={muted}>
            Возвратная логистика: −{formatRub(economics.returnExpenses ?? 0)} ₽
          </p>
        )}
        {(economics.penalties ?? 0) > 0 && (
          <p className={muted}>
            Штрафы: −{formatRub(economics.penalties ?? 0)} ₽
          </p>
        )}
        {(economics.otherAttributedExpenses ?? 0) > 0 && (
          <p className={muted}>
            Прочие: −{formatRub(economics.otherAttributedExpenses ?? 0)} ₽
          </p>
        )}
        {economics.tax && economics.tax.regime !== 'none' && taxBase != null && (
          <p className={muted}>
            Налоговая база: {formatRub(taxBase)} ₽, налог: −{formatRub(economics.tax.expense)} ₽
          </p>
        )}
        {costOfGoodsSold != null && costOfGoodsSold > 0 && (
          <p className={muted}>
            Себестоимость (COGS): −{formatRub(costOfGoodsSold)} ₽
          </p>
        )}
        {(economics.promoSpend ?? 0) > 0 && (
          <p className={muted}>
            Продвижение (Performance): −{formatRub(economics.promoSpend ?? 0)} ₽
          </p>
        )}
        {economics.profitBeforeTax != null && netProfit != null && (
          <p className={muted}>
            Чистая прибыль = чистая выручка + компенсации + корректировки − расходы Ozon − возвратная
            логистика − штрафы − прочие − COGS − продвижение − налог = {formatRub(netProfit)} ₽
          </p>
        )}
      </div>

      {economics.warnings?.map((warning) => (
        <p key={warning} className="text-xs text-amber-600 dark:text-amber-500">
          {warning}
        </p>
      ))}

      {netProfit != null && (
        <p className={muted}>
          Чистая прибыль (после налога): {formatRub(netProfit)} ₽
        </p>
      )}

      {economics.marginPercent != null && netProfit != null && netRevenue != null && netRevenue !== 0 ? (
        <p className={muted}>
          Маржа = чистая прибыль / выручка × 100% = {formatRub(netProfit)} / {formatRub(netRevenue)} ×
          100% = {economics.marginPercent.toFixed(1)}%
        </p>
      ) : null}

      {economics.roiPercent != null &&
      netProfit != null &&
      costOfGoodsSold != null &&
      costOfGoodsSold > 0 ? (
        <p className={muted}>
          ROI = чистая прибыль / себестоимость × 100% = {formatRub(netProfit)} /{' '}
          {formatRub(costOfGoodsSold)} × 100% = {economics.roiPercent.toFixed(1)}%
        </p>
      ) : null}
    </div>
  );
}
